import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { LoadingDates } from 'app/core/store/edit-relocation-detail/relocation-detail.interface';
import { ValidatorService } from '../../../core/services/validator.service';
import { RelocationDetail } from '../../../core/store/edit-florder-detail/florder-detail.interface';
import { util } from '../../../core/util/util';
import { FlorderEditPlanningComponent } from '../../florder/florder-edit-planning/florder-edit-planning.component';
import { ScrollService } from '../../../core/services/scroll.service';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { appSettings } from '../../../appSettings';
import { DateRange } from '@dlw-int-mwi/eps-common';

@Component({
  selector: 'app-relocation-edit-planning',
  templateUrl: './relocation-edit-planning.component.html',
  styleUrls: ['../../florder/florder-edit-planning/florder-edit-planning.component.scss'],
})
export class RelocationEditPlanningComponent extends FlorderEditPlanningComponent implements OnInit, OnChanges {
  orderContainsCombinations: boolean;

  labelWidth = appSettings.LABEL_WIDTH;

  @Input() relocationDetail: RelocationDetail;
  @Input() authorization: AuthorizationMatrix;
  @Input() availableLoadingDates: LoadingDates;

  constructor(formBuilder: UntypedFormBuilder, validatorService: ValidatorService, scrollService: ScrollService) {
    super(formBuilder, validatorService, scrollService);
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.relocationDetail && this.relocationDetail && this.editFlorderPlanningForm) {
      this.updateForm();
    }
  }

  ngOnInit(): any {
    super.ngOnInit();
  }

  createForm() {
    const loadingDateValidators = this.panelStatus.createTemplate || this.panelStatus.editTemplate ? null : [Validators.required, this.validatorService.datePatternValidator];
    const unloadingDateValidators = this.panelStatus.createTemplate || this.panelStatus.editTemplate ? null : [Validators.required, this.validatorService.datePatternValidator];

    this.orderContainsCombinations = this.relocationDetail && this.relocationDetail.materials && !!this.relocationDetail.materials.find(m => m.type === 'combination');
    const controlsConfig = {
      orderDate: [this.relocationDetail.planning.orderDate || undefined],
      loadingDate: [this.relocationDetail.planning.loadingDate || undefined, loadingDateValidators],
      unloadingDate: [this.relocationDetail.planning.unloadingDate || undefined],
      exchangePallets: [this.relocationDetail.planning.exchangePallets],
      numberOfExchangePallets: [this.orderContainsCombinations && (this.relocationDetail.planning.numberOfExchangePallets || '')],
      createTemplate: [this.relocationDetail.createTemplate || this.panelStatus.createTemplate || this.panelStatus.editTemplate],
      templateName: [this.relocationDetail.templateName],
      quantity: [this.relocationDetail.planning.quantity || 1, this.panelStatus.createTemplate ? [] : [Validators.required]]
    };
    this.editFlorderPlanningForm = this.formBuilder.group(controlsConfig);

    util.conditionalValidation(this.editFlorderPlanningForm, 'numberOfExchangePallets', [util.createCondition('exchangePallets')], [Validators.required, Validators.min(1)]);
    util.conditionalValidation(this.editFlorderPlanningForm, 'templateName', [util.createCondition('createTemplate')], [Validators.required]);
    const unloadingDateAfterLoadingDateValidator: ValidatorFn = (dateControl: UntypedFormControl) => {
      try {
        const unloadingDate = dateControl.value;
        if (typeof unloadingDate === 'string' && unloadingDate !== '') {
          const loadingDate = this.editFlorderPlanningForm.value.loadingDate;
          if (loadingDate && util.parseDate(loadingDate).getTime() > util.parseDate(unloadingDate).getTime()) {
            return { unloadingDateAfterLoadingDateValidator: true };
          }
        }
      } catch (e) {
        // Avoid crash in app
      }
      return null;
    };
    util.conditionalValidation(
      this.editFlorderPlanningForm,
      'unloadingDate',
      [util.createCondition('loadingDate')],
      [Validators.required, this.validatorService.datePatternValidator, unloadingDateAfterLoadingDateValidator],
      unloadingDateValidators
    );

    this.formValidChanged.emit(this.editFlorderPlanningForm.valid);
    this.editFlorderPlanningForm.statusChanges
      .pipe(
        map(data => this.editFlorderPlanningForm.valid),
        distinctUntilChanged()
      )
      .subscribe(valid => {
        this.formValidChanged.emit(valid);
      });
  }

  updateForm() {
    this.orderContainsCombinations = this.relocationDetail && this.relocationDetail.materials && !!this.relocationDetail.materials.find(m => m.type === 'combination');
    this.editFlorderPlanningForm.patchValue({
      orderDate: this.relocationDetail.planning.orderDate || undefined,
      loadingDate: this.relocationDetail.planning.loadingDate || undefined,
      unloadingDate: this.relocationDetail.planning.unloadingDate || undefined,
      exchangePallets: this.relocationDetail.planning.exchangePallets,
      numberOfExchangePallets: this.relocationDetail.planning.numberOfExchangePallets || this.relocationDetail.planning.maxAmountOfPalletExchangeAvailable,
      createTemplate: this.relocationDetail.createTemplate || this.panelStatus.createTemplate || this.panelStatus.editTemplate,
      templateName: this.relocationDetail.templateName,
    });
  }

  get validLoadingDates(): DateRange[] {
    const ranges = [];
    this.availableLoadingDates.from.forEach(date => {
      ranges.push({ startDate: date, endDate: date });
    })
    return ranges;
  }

  get validUnloadingDates(): DateRange[] {
    const ranges = [];
    this.availableLoadingDates.to.forEach(date => {
      ranges.push({ startDate: date, endDate: date });
    })
    return ranges;
  }

}
